import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import type { AnalogPricePoint, AnalogSignificantMove } from '@/mocks/stockAnalogMock';
import type { StorylineChartEvent, StorylineChartSymbol } from '@/mocks/marketStorylinesMock';
import { AnalogForwardMiniChart } from '@/components/AnalogForwardMiniChart';
import { formatPct, pctChipClass } from '@/components/AnalogLensAccordion';
import { resolveEventAnchorDate } from '@/components/StorylineSectorChart';
import type { Glp1StorylineChartEvent } from '@/services/api';

export type Glp1HistoricalPriceRow = {
  date: string;
  close: number;
  percent_change: number | null;
};

type ChartSlice = {
  symbol: string;
  label: string;
  anchorDate: string;
  anchorClose: number;
  dayReturnPct: number;
  path: AnalogPricePoint[];
  significantMoves: AnalogSignificantMove[];
};

type Props = {
  event: Pick<StorylineChartEvent, 'date' | 'summary' | 'primaryCatalyst'>;
  charts: StorylineChartSymbol[];
  pricesBySymbol: Record<string, Glp1HistoricalPriceRow[]>;
  eventsBySymbol: Record<string, Glp1StorylineChartEvent[]>;
  catalogEvents: StorylineChartEvent[];
  pricesLoading?: boolean;
  className?: string;
};

const TRADING_DAYS_BEFORE = 30;
const TRADING_DAYS_AFTER = 40;
const PRIMARY_SYMBOL = 'OZEM';
const OTHER_MOVE_MIN_ABS_PCT = 2.5;
const MAX_OTHER_PRICE_MOVES = 6;

function dayReturnPctForRow(
  rows: Glp1HistoricalPriceRow[],
  anchorDate: string,
  fallback?: number | null,
): number {
  if (fallback != null) return fallback;
  const idx = rows.findIndex((row) => row.date === anchorDate);
  if (idx < 0) return 0;
  const row = rows[idx];
  if (row.percent_change != null) return row.percent_change;
  if (idx > 0 && rows[idx - 1].close > 0) {
    return ((row.close - rows[idx - 1].close) / rows[idx - 1].close) * 100;
  }
  return 0;
}

function mergeSymbolEvents(
  apiEvents: Glp1StorylineChartEvent[] | undefined,
  catalogEvents: StorylineChartEvent[],
): StorylineChartEvent[] {
  return catalogEvents.map((catalogEvent) => {
    const apiRow = apiEvents?.find(
      (row) => row.date === catalogEvent.date && row.label === catalogEvent.label,
    );
    if (!apiRow) return catalogEvent;
    return {
      ...catalogEvent,
      summary: apiRow.summary || catalogEvent.summary,
      percentChange: apiRow.percentChange ?? catalogEvent.percentChange,
      primaryCatalyst: apiRow.primaryCatalyst ?? catalogEvent.primaryCatalyst,
      glp1Context: apiRow.glp1Context ?? catalogEvent.glp1Context,
    };
  });
}

function buildCatalystMoves(
  symbolEvents: StorylineChartEvent[],
  rows: Glp1HistoricalPriceRow[],
  chartDates: string[],
): AnalogSignificantMove[] {
  const moves: AnalogSignificantMove[] = [];
  for (const symbolEvent of symbolEvents) {
    const anchorDate = resolveEventAnchorDate(chartDates, symbolEvent.date);
    if (!anchorDate) continue;
    moves.push({
      date: anchorDate,
      percentChange: dayReturnPctForRow(rows, anchorDate, symbolEvent.percentChange),
      summary: symbolEvent.summary,
      primaryCatalyst: symbolEvent.primaryCatalyst ?? symbolEvent.label,
      sectorContext: symbolEvent.glp1Context ?? null,
    });
  }
  return moves;
}

function buildOtherPriceMoves(
  windowRows: Glp1HistoricalPriceRow[],
  chartDates: string[],
  usedDates: Set<string>,
  apiEvents?: Glp1StorylineChartEvent[],
): AnalogSignificantMove[] {
  const chartDateSet = new Set(chartDates);
  const apiByDate = new Map(
    (apiEvents ?? []).map((event) => [event.date, event] as const),
  );
  const candidates: AnalogSignificantMove[] = [];

  for (const row of windowRows) {
    if (!chartDateSet.has(row.date) || usedDates.has(row.date)) continue;
    const pct = dayReturnPctForRow(windowRows, row.date, row.percent_change);
    if (Math.abs(pct) < OTHER_MOVE_MIN_ABS_PCT) continue;
    const enriched = apiByDate.get(row.date);
    candidates.push({
      date: row.date,
      percentChange: enriched?.percentChange ?? pct,
      summary: enriched?.summary ?? `Stock moved ${formatPct(pct)} on this session.`,
      primaryCatalyst: enriched?.primaryCatalyst ?? enriched?.label ?? null,
      sectorContext: enriched?.glp1Context ?? null,
      secondaryCatalysts: enriched?.secondaryCatalysts ?? null,
    });
  }

  return candidates
    .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange))
    .slice(0, MAX_OTHER_PRICE_MOVES);
}

function buildSignificantMoves(
  symbolEvents: StorylineChartEvent[],
  rows: Glp1HistoricalPriceRow[],
  path: AnalogPricePoint[],
  windowRows: Glp1HistoricalPriceRow[],
  apiEvents?: Glp1StorylineChartEvent[],
): AnalogSignificantMove[] {
  const chartDates = path.map((point) => point.date).filter((date): date is string => Boolean(date));
  const catalystMoves = buildCatalystMoves(symbolEvents, rows, chartDates);
  const usedDates = new Set(catalystMoves.map((move) => move.date));
  const otherMoves = buildOtherPriceMoves(windowRows, chartDates, usedDates, apiEvents);

  const byDate = new Map<string, AnalogSignificantMove>();
  for (const move of [...catalystMoves, ...otherMoves]) {
    if (!byDate.has(move.date)) {
      byDate.set(move.date, move);
    }
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function buildChartSlice(
  symbol: string,
  label: string,
  rows: Glp1HistoricalPriceRow[],
  eventDate: string,
  symbolEvents: StorylineChartEvent[],
  apiEvents?: Glp1StorylineChartEvent[],
): ChartSlice | null {
  if (!rows.length) return null;

  const dates = rows.map((row) => row.date);
  const anchorDate = resolveEventAnchorDate(dates, eventDate);
  if (!anchorDate) return null;

  const anchorIdx = rows.findIndex((row) => row.date === anchorDate);
  if (anchorIdx < 0) return null;

  const startIdx = Math.max(0, anchorIdx - TRADING_DAYS_BEFORE);
  const endIdx = Math.min(rows.length - 1, anchorIdx + TRADING_DAYS_AFTER);
  const windowRows = rows.slice(startIdx, endIdx + 1);
  const anchorClose = rows[anchorIdx]?.close;
  if (!anchorClose || anchorClose <= 0) return null;

  const path: AnalogPricePoint[] = windowRows.map((row, index) => ({
    t: startIdx + index - anchorIdx,
    date: row.date,
    price: (row.close / anchorClose) * 100,
  }));

  return {
    symbol,
    label,
    anchorDate,
    anchorClose,
    dayReturnPct: dayReturnPctForRow(rows, anchorDate),
    path,
    significantMoves: buildSignificantMoves(symbolEvents, rows, path, windowRows, apiEvents),
  };
}

export function Glp1EventStockCharts({
  event,
  charts,
  pricesBySymbol,
  eventsBySymbol,
  catalogEvents,
  pricesLoading = false,
  className = '',
}: Props) {
  const chartSlices = useMemo(() => {
    const slices: ChartSlice[] = [];
    for (const chart of charts) {
      const fetchSymbol = chart.priceSymbol ?? chart.symbol;
      const rows = pricesBySymbol[fetchSymbol] ?? pricesBySymbol[chart.symbol] ?? [];
      const symbolEvents = mergeSymbolEvents(eventsBySymbol[chart.symbol], catalogEvents);
      const apiRows = eventsBySymbol[chart.symbol];
      const slice = buildChartSlice(
        chart.symbol,
        chart.label,
        rows,
        event.date,
        symbolEvents,
        apiRows,
      );
      if (slice) slices.push(slice);
    }
    return slices;
  }, [catalogEvents, charts, event.date, eventsBySymbol, pricesBySymbol]);

  const primaryChart =
    chartSlices.find((slice) => slice.symbol === (charts[0]?.symbol ?? PRIMARY_SYMBOL)) ??
    chartSlices[0] ??
    null;
  const relatedCharts = chartSlices.filter((slice) => slice.symbol !== primaryChart?.symbol);

  const summary = event.primaryCatalyst ?? event.summary;

  if (pricesLoading) {
    return (
      <div className={`flex items-center gap-2 px-2 py-8 text-sm text-gray-500 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading stock charts…
      </div>
    );
  }

  if (!primaryChart && relatedCharts.length === 0) {
    return (
      <p className={`px-2 py-4 text-sm text-gray-500 ${className}`}>
        No price history available around this date for the GLP-1 watchlist.
      </p>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {summary ? (
        <p className="px-2 text-xs leading-relaxed text-gray-500">{summary}</p>
      ) : null}

      {primaryChart ? (
        <div>
          <div className="mb-2 flex items-center gap-2 px-2">
            <p className="text-xs font-medium text-gray-500">{primaryChart.label}</p>
            <span className={`text-xs font-medium ${pctChipClass(primaryChart.dayReturnPct)}`}>
              {formatPct(primaryChart.dayReturnPct)}
            </span>
          </div>
          <AnalogForwardMiniChart
            path={primaryChart.path}
            anchorDate={primaryChart.anchorDate}
            anchorClose={primaryChart.anchorClose}
            significantMoves={primaryChart.significantMoves}
          />
        </div>
      ) : null}

      {relatedCharts.length > 0 ? (
        <div className="space-y-4 border-t border-gray-100 pt-3">
          <p className="px-2 text-xs font-medium text-gray-500">Key names</p>
          {relatedCharts.map((chart) => (
            <div key={chart.symbol}>
              <div className="mb-1 flex items-center gap-2 px-2">
                <p className="text-xs text-gray-500">{chart.label}</p>
                <span className={`text-xs font-medium ${pctChipClass(chart.dayReturnPct)}`}>
                  {formatPct(chart.dayReturnPct)}
                </span>
              </div>
              <AnalogForwardMiniChart
                path={chart.path}
                anchorDate={chart.anchorDate}
                anchorClose={chart.anchorClose}
                significantMoves={chart.significantMoves}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
